import * as Sentry from "@sentry/react";
import React from "react";
import {
  useClaimInvitationPairingMutation,
  useCreateInvitationPairingMutation,
} from "./useInvitationPairing.generated.js";

const POLL_INTERVAL_MS = 2000;
const RETRY_CREATE_MS = 5000;

export type InvitationPairingStatus =
  | "creating"
  | "active"
  | "error"
  | "claimed";

type State = {
  status: InvitationPairingStatus;
  code: string | null;
  refToken: string | null;
  /** Hand-over grant from the claim; null when the backend issued none. */
  passwordlessSignupGrant: string | null;
  /** Pairing credentials, exposed only once claimed so the page can store them. */
  pairingId: string | null;
  secret: string | null;
};

const INITIAL: State = {
  status: "creating",
  code: null,
  refToken: null,
  passwordlessSignupGrant: null,
  pairingId: null,
  secret: null,
};

/**
 * Owns the create -> poll -> renew -> claimed cycle of an invitation pairing.
 * The secret lives in this hook's closure and is exposed only after the claim,
 * for the page's tab-scoped stored hand-over.
 */
export function useInvitationPairing({
  enabled,
}: {
  enabled: boolean;
}): State & {
  /** Discards the current pairing and starts a fresh create -> poll cycle. */
  restart: () => void;
} {
  const [generation, setGeneration] = React.useState(0);
  const restart = React.useCallback(() => {
    setState(INITIAL);
    setGeneration(g => g + 1);
  }, []);
  // The secret (create) and the refToken (claim) are bearer credentials: keep
  // them out of the shared Apollo cache, which would outlive this hook.
  const [createPairing] = useCreateInvitationPairingMutation({
    fetchPolicy: "no-cache",
  });
  const [claimPairing] = useClaimInvitationPairingMutation({
    fetchPolicy: "no-cache",
  });
  const createRef = React.useRef(createPairing);
  const claimRef = React.useRef(claimPairing);
  createRef.current = createPairing;
  claimRef.current = claimPairing;

  const [state, setState] = React.useState<State>(INITIAL);

  React.useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = (fn: () => Promise<void>, ms: number) => {
      timer = setTimeout(() => {
        void fn();
      }, ms);
    };

    const create = async (): Promise<void> => {
      try {
        const result = await createRef.current();
        if (cancelled) return;
        const pairing = result.data?.createInvitationPairing;
        if (!pairing) throw new Error("No pairing returned");
        const expiresAt = new Date(pairing.expiresAt).getTime();
        setState({ ...INITIAL, status: "active", code: pairing.code });

        const poll = async (): Promise<void> => {
          if (Date.now() >= expiresAt) {
            await create();
            return;
          }
          try {
            const claim = await claimRef.current({
              variables: {
                input: { pairingId: pairing.id, secret: pairing.secret },
              },
            });
            if (cancelled) return;
            const data = claim.data?.claimInvitationPairing;
            if (data?.status === "CLAIMED" && data.refToken) {
              setState({
                status: "claimed",
                code: pairing.code,
                refToken: data.refToken,
                passwordlessSignupGrant: data.passwordlessSignupGrant ?? null,
                pairingId: pairing.id,
                secret: pairing.secret,
              });
              return;
            }
            if (data?.status === "UNAVAILABLE") {
              await create();
              return;
            }
          } catch (error) {
            Sentry.captureException(error);
          }
          if (!cancelled) schedule(poll, POLL_INTERVAL_MS);
        };
        schedule(poll, POLL_INTERVAL_MS);
      } catch (error) {
        if (cancelled) return;
        Sentry.captureException(error);
        setState({ ...INITIAL, status: "error" });
        schedule(create, RETRY_CREATE_MS);
      }
    };

    void create();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, generation]);

  return { ...state, restart };
}
